import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/registry/reapit/lib/utils'

// DS Text input spec:
//   Small  — h-8  (32px), px-2.5, text-xs  (12px, compact variant)
//   Medium — h-9  (36px), px-3,   text-base (16px, default)
//   Large  — h-10 (40px), px-4,   text-base (16px)
//
// Placeholder colour: --text-placeholder (neutral-300, #9FAEBC) per DS colour.text.placeholder
// Border: --input (neutral-150, #D8DEE4) at rest; --ring (purple-300) on focus
// Disabled: opacity-50 + bg-muted + no pointer events
const inputVariants = cva(
  'w-full min-w-0 bg-transparent text-base file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-text-placeholder disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 read-only:bg-muted/50 read-only:cursor-default selection:bg-primary selection:text-primary-foreground',
  {
    variants: {
      size: {
        small: 'h-8 px-2.5 py-1 text-xs',
        medium: 'h-9 px-3 py-1 text-base',
        large: 'h-10 px-4 py-2 text-base',
      },
    },
    defaultVariants: {
      size: 'medium',
    },
  },
)

type InputProps = Omit<React.ComponentProps<'input'>, 'size'> &
  VariantProps<typeof inputVariants> & {
    prefix?: React.ReactNode
    suffix?: React.ReactNode
  }

function Input({ className, type, size, prefix, suffix, ...props }: InputProps) {
  if (prefix != null || suffix != null) {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 rounded-md border border-input bg-transparent shadow-xs transition-[color,box-shadow]',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
          'has-[input[aria-invalid]]:border-destructive has-[input[aria-invalid]]:ring-destructive/20',
          'has-[input:disabled]:opacity-50 has-[input:disabled]:cursor-not-allowed dark:bg-input/30',
        )}
      >
        {prefix != null && (
          // DS: prefix uses body text size (text-base = 16px) matching the input value text
          <div
            data-slot="input-prefix"
            className="flex items-center pl-3 pr-2 text-base text-muted-foreground border-r border-input shrink-0"
          >
            {prefix}
          </div>
        )}
        <input
          type={type}
          data-slot="input"
          className={cn(
            inputVariants({ size }),
            'flex-1 rounded-none border-0 shadow-none outline-none focus-visible:ring-0',
            className,
          )}
          {...props}
        />
        {suffix != null && (
          // DS: suffix uses body text size (text-base = 16px) matching the input value text
          <div
            data-slot="input-suffix"
            className="flex items-center pl-2 pr-3 text-base text-muted-foreground border-l border-input shrink-0"
          >
            {suffix}
          </div>
        )}
      </div>
    )
  }

  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        inputVariants({ size }),
        'rounded-md border border-input shadow-xs transition-[color,box-shadow] outline-none',
        'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
        'dark:bg-input/30',
        className,
      )}
      {...props}
    />
  )
}

export { Input, inputVariants }
export type { InputProps }
